import { buildDiagnosisPrompt, buildStepSimplifyPrompt, streamChat } from "../ai/client";
import {
  DIAGNOSIS_PROGRESS,
  extractDiagnosisProgressStatus,
  extractPartialFullTranslation,
  extractPartialSummary,
  parseDiagnosisResponse,
} from "../ai/parseDiagnosis";
import {
  completeLearningStep,
  getReading,
  listAnnotations,
  listDiagnosisBlocks,
  listLearningSteps,
  recordDiagnosisComplete,
  recordReadingCompletedWithoutSimplify,
  recordSimplifyRequest,
  recordStepCompleted,
  replaceDiagnosisBlocks,
  replaceLearningSteps,
  resolveReadingLevel,
  updateLearningStep,
  updateReadingStatus,
} from "../db";
import { navigate } from "../router";
import { downloadReadingReport } from "../export/report";
import type { DiagnosisBlock, DiagnosisType, LearningStep, ReadingRecord, Route, SelectionAnnotation } from "../types";
import { getViewEl } from "../ui/layout";
import { renderMarkdown, setMarkdownContent } from "../ui/markdown";
import { attachSelectionAsk, renderAnnotatedText, type SelectionAskHandle } from "../ui/selectionAsk";
import {
  clearSourceHighlights,
  highlightExcerptInElement,
  scrollHighlightIntoView,
} from "../ui/sourceHighlight";
import { esc, toast } from "../ui/toast";

let activeAbort: AbortController | null = null;
let selectionAskHandle: SelectionAskHandle | null = null;
let cachedAnnotations: SelectionAnnotation[] = [];
let activeBlockIndex: number | null = null;
let blockHighlightCleanup: (() => void) | null = null;

function stopSelectionAsk(): void {
  selectionAskHandle?.detach();
  selectionAskHandle = null;
}

function stopBlockHighlightBindings(): void {
  blockHighlightCleanup?.();
  blockHighlightCleanup = null;
}

function refreshSourceBody(view: HTMLElement, text: string): void {
  const body = view.querySelector<HTMLElement>(".diagnosis-source-body");
  if (!body) {
    return;
  }
  const selectionNote = body.querySelector<HTMLElement>(".diagnosis-selection-note");
  body.innerHTML = `
    <p class="diagnosis-text">${renderAnnotatedText(text, cachedAnnotations)}</p>
    ${selectionNote ? selectionNote.outerHTML : ""}
  `;

  if (activeBlockIndex !== null) {
    const block = view.querySelector<HTMLElement>(
      `.block-item[data-block-index="${activeBlockIndex}"]`,
    );
    const excerpt = block?.dataset.excerpt;
    if (excerpt) {
      const textEl = body.querySelector<HTMLElement>(".diagnosis-text");
      if (textEl) {
        const highlight = highlightExcerptInElement(textEl, text, excerpt);
        scrollHighlightIntoView(highlight);
      }
    }
  }
}

function refreshAnnotationsSidebar(view: HTMLElement): void {
  const panel = view.querySelector<HTMLElement>("#annotationsPanel");
  if (!panel) {
    return;
  }
  panel.innerHTML = renderAnnotationsSidebar(cachedAnnotations);
  bindAnnotationsSidebar(view);
}

function formatAnnotationDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso.slice(0, 10);
  }
  return date.toLocaleDateString("zh-CN", { month: "short", day: "numeric" });
}

function snippetPreview(text: string, max = 48): string {
  const trimmed = text.replace(/\s+/g, " ").trim();
  if (trimmed.length <= max) {
    return trimmed;
  }
  return `${trimmed.slice(0, max)}?`;
}

function renderAnnotationsSidebar(annotations: SelectionAnnotation[]): string {
  if (!annotations.length) {
    return `
      <details class="annotations-sidebar" open>
        <summary class="annotations-sidebar-summary">
          <span class="annotations-sidebar-title">????</span>
          <span class="badge annotations-count">0</span>
        </summary>
        <p class="muted small annotations-empty">???????????????????? AI??????</p>
      </details>`;
  }

  return `
    <details class="annotations-sidebar" open>
      <summary class="annotations-sidebar-summary">
        <span class="annotations-sidebar-title">????</span>
        <span class="badge annotations-count">${annotations.length}</span>
      </summary>
      <ul class="annotation-list">
        ${annotations
          .map(
            (ann) => `
          <li>
            <button
              type="button"
              class="annotation-list-item"
              data-annotation-id="${esc(ann.id)}"
              title="${esc(ann.selectedText)}"
            >
              <span class="annotation-list-snippet">${esc(snippetPreview(ann.selectedText))}</span>
              <span class="annotation-list-date muted small">${esc(formatAnnotationDate(ann.createdAt))}</span>
            </button>
          </li>`,
          )
          .join("")}
      </ul>
    </details>`;
}

const BLOCK_LABEL: Record<DiagnosisType, string> = {
  vocab: "??",
  phrase: "??",
  sentence: "??",
  logic: "??",
  background: "??",
};

const STATUS_LABEL: Record<string, string> = {
  draft: "??",
  diagnosing: "???",
  learning: "???",
  done: "???",
};

function stopActiveStream(): void {
  activeAbort?.abort();
  activeAbort = null;
}

function activeStepIndex(reading: ReadingRecord, steps: LearningStep[]): number {
  if (steps.length === 0) {
    return 0;
  }
  if (reading.status === "done") {
    return steps.length - 1;
  }
  return Math.min(Math.max(reading.currentStep, 0), steps.length - 1);
}

function renderBlocks(blocks: DiagnosisBlock[]): string {
  if (!blocks.length) {
    return `<p class="muted">??????????????</p>`;
  }
  return `<ul class="block-list">
    ${blocks
      .map(
        (block, index) => `
      <li
        class="block-item block-item-linkable"
        data-block-index="${index}"
        data-excerpt="${esc(block.excerpt)}"
        role="button"
        tabindex="0"
        title="????????"
      >
        <span class="badge badge-block badge-${esc(block.type)}">${esc(BLOCK_LABEL[block.type])}</span>
        <p class="block-excerpt">${esc(block.excerpt)}</p>
        ${
          block.translation
            ? `<p class="block-translation muted">???${esc(block.translation)}</p>`
            : ""
        }
        <div class="block-note markdown">${renderMarkdown(block.note)}</div>
      </li>`,
      )
      .join("")}
  </ul>`;
}

function renderStreamingPanel(
  partialSummary: string | null,
  partialTranslation: string | null,
  statusText: string,
): string {
  const previewParts: string[] = [];
  if (partialSummary) {
    previewParts.push(`<div class="streaming-preview">
        <p class="streaming-preview-label">\u521d\u6b65\u5206\u6790</p>
        <p class="streaming-preview-text">${esc(partialSummary)}</p>
      </div>`);
  }
  if (partialTranslation) {
    previewParts.push(`<div class="streaming-preview">
        <p class="streaming-preview-label">\u8bd1\u6587\u9884\u89c8</p>
        <p class="streaming-preview-text">${esc(partialTranslation)}</p>
      </div>`);
  }

  const preview =
    previewParts.length > 0
      ? previewParts.join("")
      : `<div class="streaming-skeleton" aria-hidden="true">
        <div class="skeleton-line"></div>
        <div class="skeleton-line short"></div>
        <div class="skeleton-card"></div>
        <div class="skeleton-card"></div>
      </div>`;

  return `
    <div class="streaming-status">
      <span class="streaming-spinner" aria-hidden="true"></span>
      <span class="streaming-status-text">${esc(statusText)}</span>
    </div>
    ${preview}`;
}

function renderParseErrorPanel(raw: string): string {
  return `
    <div class="diagnosis-parse-error">
      <p class="diagnosis-parse-error-title">????????</p>
      <p class="muted small">AI ??????????????????????????????????</p>
      <details class="diagnosis-raw-toggle">
        <summary>??????</summary>
        <pre class="diagnosis-raw-output">${esc(raw)}</pre>
      </details>
    </div>`;
}

function updateStreamingOutput(
  outputEl: HTMLElement,
  aggregated: string,
  onStatus?: (text: string) => void,
): void {
  const statusText = extractDiagnosisProgressStatus(aggregated);
  const partialSummary = extractPartialSummary(aggregated);
  const partialTranslation = extractPartialFullTranslation(aggregated);
  outputEl.innerHTML = renderStreamingPanel(partialSummary, partialTranslation, statusText);
  onStatus?.(statusText);
}

function renderTranslationSection(textTranslation: string | null): string {
  if (!textTranslation?.trim()) {
    return "";
  }
  return `
      <div class="diagnosis-translation">
        <h2>\u8bd1\u6587</h2>
        <div class="diagnosis-translation-body markdown">${renderMarkdown(textTranslation)}</div>
      </div>`;
}

function setDiagnosisSectionsVisible(view: HTMLElement, visible: boolean): void {
  view.querySelectorAll<HTMLElement>(".summary-card, .blocks-panel, .wizard-panel").forEach((el) => {
    el.hidden = !visible;
  });
}

function setActiveBlock(view: HTMLElement, index: number | null): void {
  activeBlockIndex = index;
  view.querySelectorAll<HTMLElement>(".block-item-linkable").forEach((item) => {
    const itemIndex = Number(item.dataset.blockIndex);
    item.classList.toggle("is-active", index !== null && itemIndex === index);
  });
}

function clearBlockHighlight(view: HTMLElement): void {
  setActiveBlock(view, null);
  clearSourceHighlights(view);
}

function activateBlockHighlight(view: HTMLElement, readingText: string, index: number, excerpt: string): void {
  setActiveBlock(view, index);
  const textEl = view.querySelector<HTMLElement>(".diagnosis-text");
  if (!textEl) {
    return;
  }
  const highlight = highlightExcerptInElement(textEl, readingText, excerpt);
  scrollHighlightIntoView(highlight);
}

function bindBlockHighlights(view: HTMLElement, readingText: string): void {
  stopBlockHighlightBindings();

  const onBlockActivate = (item: HTMLElement): void => {
    const index = Number(item.dataset.blockIndex);
    const excerpt = item.dataset.excerpt;
    if (!excerpt || Number.isNaN(index)) {
      return;
    }

    if (activeBlockIndex === index) {
      clearBlockHighlight(view);
      return;
    }

    activateBlockHighlight(view, readingText, index, excerpt);
  };

  const onBlockClick = (event: Event): void => {
    const item = (event.target as Element).closest<HTMLElement>(".block-item-linkable");
    if (!item || !view.contains(item)) {
      return;
    }
    event.preventDefault();
    onBlockActivate(item);
  };

  const onBlockKeydown = (event: KeyboardEvent): void => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    const item = (event.target as Element).closest<HTMLElement>(".block-item-linkable");
    if (!item || !view.contains(item)) {
      return;
    }
    event.preventDefault();
    onBlockActivate(item);
  };

  const onDocumentMouseDown = (event: MouseEvent): void => {
    const target = event.target as Element;
    if (target.closest(".block-item-linkable")) {
      return;
    }
    if (activeBlockIndex === null) {
      return;
    }
    clearBlockHighlight(view);
  };

  view.addEventListener("click", onBlockClick);
  view.addEventListener("keydown", onBlockKeydown);
  document.addEventListener("mousedown", onDocumentMouseDown);

  blockHighlightCleanup = () => {
    view.removeEventListener("click", onBlockClick);
    view.removeEventListener("keydown", onBlockKeydown);
    document.removeEventListener("mousedown", onDocumentMouseDown);
  };
}

function bindAnnotationsSidebar(view: HTMLElement): void {
  view.querySelectorAll<HTMLButtonElement>(".annotation-list-item").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.dataset.annotationId;
      if (!id) {
        return;
      }

      view.querySelectorAll<HTMLElement>(".annotation-mark.is-active").forEach((mark) => {
        mark.classList.remove("is-active");
      });

      const mark = view.querySelector<HTMLElement>(`.annotation-mark[data-annotation-id="${id}"]`);
      if (mark) {
        mark.classList.add("is-active");
        mark.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      view.querySelectorAll<HTMLButtonElement>(".annotation-list-item").forEach((item) => {
        item.classList.toggle("is-active", item.dataset.annotationId === id);
      });

      void selectionAskHandle?.openAnnotationById(id);
    });
  });
}

function renderWizard(
  reading: ReadingRecord,
  steps: LearningStep[],
  stepIndex: number,
  streaming = false,
): string {
  if (!steps.length) {
    return `<p class="muted">?????????????</p>`;
  }

  if (reading.status === "done") {
    return `
      <div class="wizard-complete">
        <p class="wizard-complete-title">???????</p>
        <p class="muted">????????????????????????</p>
      </div>`;
  }

  const step = steps[stepIndex];
  if (!step) {
    return `<p class="muted">?????????????</p>`;
  }

  const progress = ((stepIndex + 1) / steps.length) * 100;

  return `
    <div class="wizard-progress" aria-hidden="true">
      <div class="wizard-progress-bar" style="width: ${progress}%"></div>
    </div>
    <p class="wizard-meta">?? ${stepIndex + 1} / ${steps.length}</p>
    <h3 class="wizard-title" id="wizardStepTitle">${esc(step.title)}</h3>
    <div class="wizard-content markdown" id="wizardStepContent">${renderMarkdown(step.content)}</div>
    ${
      step.checkQuestion
        ? `<div class="wizard-check" id="wizardCheckQuestion">
            <span class="wizard-check-label">??</span>
            <div class="markdown">${renderMarkdown(step.checkQuestion)}</div>
          </div>`
        : ""
    }
    <div class="wizard-actions">
      <button type="button" class="btn btn-primary" id="btnStepNext" ${streaming ? "disabled" : ""}>
        ??????
      </button>
      <button type="button" class="btn" id="btnStepExplain" ${streaming ? "disabled" : ""}>
        ?????
      </button>
    </div>
    <p class="muted small" id="wizardStatus"></p>`;
}

type PersistDiagnosisResult =
  | { ok: true; reading: ReadingRecord; blocks: DiagnosisBlock[]; steps: LearningStep[] }
  | { ok: false; raw: string; reading: ReadingRecord };

async function persistDiagnosis(readingId: string, raw: string): Promise<PersistDiagnosisResult> {
  const parsed = parseDiagnosisResponse(raw);

  if (parsed.ok) {
    const [blocks, steps] = await Promise.all([
      replaceDiagnosisBlocks(readingId, parsed.data.blocks),
      replaceLearningSteps(readingId, parsed.data.steps),
    ]);
    const reading = await updateReadingStatus(readingId, steps.length ? "learning" : "draft", {
      diagnosisSummary: parsed.data.summary,
      textTranslation: parsed.data.fullTranslation,
      currentStep: 0,
    });
    await recordDiagnosisComplete({
      suggestedLearnerLevel: parsed.data.suggestedLearnerLevel,
      learnerLevelReason: parsed.data.learnerLevelReason,
    });
    return { ok: true, reading, blocks, steps };
  }

  const current = await getReading(readingId);
  const hasExisting = Boolean(current?.diagnosisSummary?.trim());
  const reading = await updateReadingStatus(readingId, hasExisting ? "learning" : "draft");
  await recordDiagnosisComplete({});
  return { ok: false, raw: parsed.raw, reading };
}

async function runDiagnosis(
  readingId: string,
  view: HTMLElement,
  onStatus: (text: string) => void,
  onChunk: (text: string) => void,
  controls: {
    startBtn: HTMLButtonElement | null;
    abortBtn: HTMLButtonElement | null;
    retryBtn: HTMLButtonElement | null;
  },
  hadDiagnosis: boolean,
): Promise<void> {
  const reading = await getReading(readingId);
  if (!reading) {
    toast("???????");
    navigate("library");
    return;
  }

  stopActiveStream();
  const abort = new AbortController();
  activeAbort = abort;

  if (controls.startBtn) {
    controls.startBtn.disabled = true;
  }
  if (controls.abortBtn) {
    controls.abortBtn.hidden = false;
    controls.abortBtn.disabled = false;
  }
  if (controls.retryBtn) {
    controls.retryBtn.hidden = true;
  }

  onStatus(DIAGNOSIS_PROGRESS.reading);
  onChunk("");
  setDiagnosisSectionsVisible(view, false);

  await updateReadingStatus(readingId, "diagnosing");

  try {
    const effectiveLevel = await resolveReadingLevel(reading.level);
    const messages = buildDiagnosisPrompt(reading.text, reading.selection, effectiveLevel);
    const raw = await streamChat(messages, {
      signal: abort.signal,
      onChunk: (aggregated) => {
        onChunk(aggregated);
      },
    });

    onStatus(DIAGNOSIS_PROGRESS.finalizing);
    const result = await persistDiagnosis(readingId, raw);
    if (result.ok) {
      onStatus("????");
      await renderDiagnosisPage({ name: "diagnosis", focus: null, readingId });
      return;
    }

    const outputEl = view.querySelector<HTMLElement>("#diagnosisStream");
    if (outputEl) {
      outputEl.innerHTML = renderParseErrorPanel(result.raw);
    }
    setDiagnosisSectionsVisible(view, hadDiagnosis);
    onStatus("????");
    if (controls.startBtn) {
      controls.startBtn.disabled = false;
    }
    if (controls.retryBtn) {
      controls.retryBtn.hidden = false;
    }
    toast("????????????");
  } catch (err) {
    if (abort.signal.aborted) {
      onStatus("???");
      const current = await getReading(readingId);
      if (current?.status === "diagnosing") {
        const hasExisting = Boolean(current.diagnosisSummary?.trim());
        await updateReadingStatus(readingId, hasExisting ? "learning" : "draft");
      }
      setDiagnosisSectionsVisible(view, hadDiagnosis);
      const streamPanel = view.querySelector<HTMLElement>("#streamPanel");
      if (streamPanel && hadDiagnosis) {
        streamPanel.hidden = true;
      }
      if (controls.startBtn) {
        controls.startBtn.disabled = false;
      }
      if (controls.abortBtn) {
        controls.abortBtn.hidden = true;
      }
      return;
    }

    const current = await getReading(readingId);
    if (current?.status === "diagnosing") {
      const hasExisting = Boolean(current.diagnosisSummary?.trim());
      await updateReadingStatus(readingId, hasExisting ? "learning" : "draft");
    }
    setDiagnosisSectionsVisible(view, hadDiagnosis);

    onStatus("????");
    if (controls.startBtn) {
      controls.startBtn.disabled = false;
    }
    if (controls.abortBtn) {
      controls.abortBtn.hidden = true;
    }
    if (controls.retryBtn) {
      controls.retryBtn.hidden = false;
    }
    toast(err instanceof Error ? err.message : "????");
  } finally {
    if (activeAbort === abort) {
      activeAbort = null;
    }
    if (controls.abortBtn && !abort.signal.aborted) {
      controls.abortBtn.hidden = true;
    }
  }
}

async function runStepSimplify(
  reading: ReadingRecord,
  step: LearningStep,
  contentEl: HTMLElement,
  statusEl: HTMLElement,
  explainBtn: HTMLButtonElement,
  nextBtn: HTMLButtonElement,
): Promise<void> {
  stopActiveStream();
  const abort = new AbortController();
  activeAbort = abort;

  explainBtn.disabled = true;
  nextBtn.disabled = true;
  statusEl.textContent = "??????????";

  try {
    const effectiveLevel = await resolveReadingLevel(reading.level);
    const messages = buildStepSimplifyPrompt(
      step.title,
      step.content,
      step.checkQuestion,
      effectiveLevel,
    );
    const simplified = await streamChat(messages, {
      signal: abort.signal,
      onChunk: (aggregated) => {
        setMarkdownContent(contentEl, aggregated);
      },
    });

    await updateLearningStep(step.id, { content: simplified });
    setMarkdownContent(contentEl, simplified);
    await recordSimplifyRequest();
    statusEl.textContent = "?????";
  } catch (err) {
    if (!abort.signal.aborted) {
      statusEl.textContent = "";
      toast(err instanceof Error ? err.message : "????");
    }
  } finally {
    explainBtn.disabled = false;
    nextBtn.disabled = false;
    if (activeAbort === abort) {
      activeAbort = null;
    }
  }
}

function bindWizard(
  view: HTMLElement,
  reading: ReadingRecord,
  steps: LearningStep[],
  stepIndex: number,
): void {
  const nextBtn = view.querySelector<HTMLButtonElement>("#btnStepNext");
  const explainBtn = view.querySelector<HTMLButtonElement>("#btnStepExplain");
  const contentEl = view.querySelector<HTMLElement>("#wizardStepContent");
  const statusEl = view.querySelector<HTMLElement>("#wizardStatus");
  const step = steps[stepIndex];
  let simplifyUsedInSession = false;

  nextBtn?.addEventListener("click", async () => {
    if (!step) {
      return;
    }
    nextBtn.disabled = true;
    explainBtn?.setAttribute("disabled", "true");
    try {
      const { reading: updated, steps: refreshed } = await completeLearningStep(
        reading.id,
        step.stepIndex,
      );
      await recordStepCompleted();
      if (updated.status === "done" && !simplifyUsedInSession) {
        await recordReadingCompletedWithoutSimplify();
      }
      if (updated.status === "done") {
        await renderDiagnosisPage({ name: "diagnosis", focus: null, readingId: reading.id });
        return;
      }
      const wizardPanel = view.querySelector<HTMLElement>("#wizardPanel");
      if (wizardPanel) {
        const nextIndex = activeStepIndex(updated, refreshed);
        wizardPanel.innerHTML = `<h2>????</h2>${renderWizard(updated, refreshed, nextIndex)}`;
        bindWizard(view, updated, refreshed, nextIndex);
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : "??????");
      nextBtn.disabled = false;
      explainBtn?.removeAttribute("disabled");
    }
  });

  explainBtn?.addEventListener("click", async () => {
    if (!step || !contentEl || !statusEl) {
      return;
    }
    simplifyUsedInSession = true;
    await runStepSimplify(reading, step, contentEl, statusEl, explainBtn, nextBtn!);
  });
}

function bindDiagnosisControls(
  view: HTMLElement,
  readingId: string,
  hasDiagnosis: boolean,
  autoStart: boolean,
): void {
  const outputEl = view.querySelector<HTMLElement>("#diagnosisStream");
  const statusEl = view.querySelector<HTMLElement>("#diagnosisStatus");
  const startBtn = view.querySelector<HTMLButtonElement>("#btnStartDiagnosis");
  const abortBtn = view.querySelector<HTMLButtonElement>("#btnAbortDiagnosis");
  const retryBtn = view.querySelector<HTMLButtonElement>("#btnRetryDiagnosis");

  const controls = { startBtn, abortBtn, retryBtn };

  const run = async () => {
    if (!outputEl || !statusEl) {
      return;
    }
    const streamPanel = view.querySelector<HTMLElement>("#streamPanel");
    if (streamPanel) {
      streamPanel.hidden = false;
    }
    outputEl.innerHTML = renderStreamingPanel(null, null, DIAGNOSIS_PROGRESS.reading);
    statusEl.textContent = DIAGNOSIS_PROGRESS.reading;
    await runDiagnosis(
      readingId,
      view,
      (text) => {
        statusEl.textContent = text;
      },
      (text) => {
        updateStreamingOutput(outputEl, text, (status) => {
          statusEl.textContent = status;
        });
      },
      controls,
      hasDiagnosis,
    );
  };

  startBtn?.addEventListener("click", run);
  retryBtn?.addEventListener("click", run);
  abortBtn?.addEventListener("click", () => {
    stopActiveStream();
  });

  if (autoStart) {
    void run();
  } else if (hasDiagnosis && statusEl) {
    statusEl.textContent = STATUS_LABEL[view.dataset.status ?? ""] ?? "";
  }
}

export async function renderDiagnosisPage(route: Route): Promise<void> {
  stopActiveStream();
  stopSelectionAsk();
  stopBlockHighlightBindings();
  activeBlockIndex = null;

  const view = getViewEl();
  if (!view) {
    return;
  }

  const readingId = route.readingId;
  if (!readingId) {
    navigate("library");
    return;
  }

  const reading = await getReading(readingId);
  if (!reading) {
    toast("???????");
    navigate("library");
    return;
  }

  const [blocks, steps, annotations] = await Promise.all([
    listDiagnosisBlocks(readingId),
    listLearningSteps(readingId),
    listAnnotations(readingId),
  ]);
  cachedAnnotations = annotations;

  const hasDiagnosis = Boolean(reading.diagnosisSummary?.trim());
  const autoStart = reading.status === "draft" && !hasDiagnosis;
  const stepIndex = activeStepIndex(reading, steps);

  view.dataset.status = reading.status;
  view.innerHTML = `
    <div class="page-head">
      <div>
        <h1>AI ??</h1>
        <p class="lead">${esc(reading.title)}</p>
        <p class="meta">
          <span class="badge badge-status badge-${esc(reading.status)}">${esc(STATUS_LABEL[reading.status] ?? reading.status)}</span>
          ${
            steps.length
              ? reading.status === "done"
                ? `<span class="muted small">???? ${steps.length} / ${steps.length}</span>`
                : `<span class="muted small">???? ${reading.currentStep + 1} / ${steps.length}</span>`
              : ""
          }
        </p>
      </div>
      <div class="page-head-actions">
        <button type="button" class="btn" id="btnExportReport">????</button>
        <button type="button" class="btn" id="btnEditReading">??</button>
        <button type="button" class="btn" id="btnBackLibrary">????</button>
      </div>
    </div>

    <section class="panel diagnosis-panel">
      <div class="diagnosis-source">
        <h2>??</h2>
        <div class="diagnosis-source-body">
          <p class="diagnosis-text">${renderAnnotatedText(reading.text, annotations)}</p>
          ${
            reading.selection
              ? `<p class="muted small diagnosis-selection-note">?????${esc(reading.selection)}</p>`
              : ""
          }
        </div>
      </div>
      ${renderTranslationSection(reading.textTranslation)}
      <div class="diagnosis-actions">
        <button type="button" class="btn btn-primary" id="btnStartDiagnosis">
          ${hasDiagnosis ? "????" : "????"}
        </button>
        <button type="button" class="btn" id="btnAbortDiagnosis" hidden>??</button>
        <button type="button" class="btn" id="btnRetryDiagnosis" hidden>??</button>
        <span class="muted small" id="diagnosisStatus"></span>
      </div>
      <div id="streamPanel" class="stream-panel" ${hasDiagnosis && !autoStart ? "hidden" : ""}>
        <div class="diagnosis-streaming" id="diagnosisStream" aria-live="polite">${
          autoStart ? renderStreamingPanel(null, null, DIAGNOSIS_PROGRESS.reading) : ""
        }</div>
      </div>
    </section>

    <section class="panel annotations-panel" id="annotationsPanel">
      ${renderAnnotationsSidebar(annotations)}
    </section>

    ${
      hasDiagnosis
        ? `
      <section class="panel summary-card">
        <h2>????</h2>
        <div class="summary-text markdown">${renderMarkdown(reading.diagnosisSummary ?? "")}</div>
      </section>

      <section class="panel blocks-panel">
        <h2>????</h2>
        ${renderBlocks(blocks)}
      </section>

      <section class="panel wizard-panel" id="wizardPanel">
        <h2>????</h2>
        ${renderWizard(reading, steps, stepIndex)}
      </section>`
        : ""
    }
  `;

  view.querySelector<HTMLButtonElement>("#btnExportReport")?.addEventListener("click", () => {
    try {
      downloadReadingReport(reading, blocks, steps, annotations);
      toast("??? Markdown");
    } catch (err) {
      toast(err instanceof Error ? err.message : "????");
    }
  });

  view.querySelector<HTMLButtonElement>("#btnEditReading")?.addEventListener("click", () => {
    stopActiveStream();
    stopSelectionAsk();
    navigate("read", null, readingId);
  });

  view.querySelector<HTMLButtonElement>("#btnBackLibrary")?.addEventListener("click", () => {
    stopActiveStream();
    stopSelectionAsk();
    navigate("library");
  });

  const effectiveLevel = await resolveReadingLevel(reading.level);
  selectionAskHandle = attachSelectionAsk({
    root: view,
    readingContext: reading.text,
    effectiveLevel,
    readingId,
    onAnnotationSaved: async () => {
      cachedAnnotations = await listAnnotations(readingId);
      refreshSourceBody(view, reading.text);
      refreshAnnotationsSidebar(view);
    },
  });

  bindAnnotationsSidebar(view);
  bindDiagnosisControls(view, readingId, hasDiagnosis, autoStart);

  if (hasDiagnosis && blocks.length) {
    bindBlockHighlights(view, reading.text);
  }

  if (hasDiagnosis && steps.length && reading.status !== "done") {
    bindWizard(view, reading, steps, stepIndex);
  }
}
